import React from 'react';
import { useIntl } from 'react-intl';
import type { TabbedApp } from '../../pages/Apps/appTabs';
import { presentationOf } from '../../pages/Apps/appPresentation';
import { localized } from '../../pages/Apps/appRunView';
import { useAppCover } from '../../pages/Apps/useAppCover';
import {
  Cover,
  Face,
  Glyph,
  Name,
  OpenPill,
  PillButton,
  Reveal,
  RevealRow,
  RevealText,
  TileRoot,
} from './FooterTile.styled';

interface FooterAppTileProps {
  readonly app: TabbedApp;
  readonly grow: boolean;
  readonly onOpen: () => void;
  readonly onAllApps?: () => void;
}

const FooterAppTile: React.FC<FooterAppTileProps> = ({ app, grow, onOpen, onAllApps }) => {
  const intl = useIntl();
  const { icon: Icon } = presentationOf(app.manifest);
  const { cover, onCoverError } = useAppCover(app.manifest);
  const name = localized(intl, app.manifest.name);
  const tagline = localized(intl, app.manifest.tagline);

  return (
    <TileRoot $grow={grow} data-testid={`apps-footer__tile-${app.appKey}`}>
      <Face
        type="button"
        onClick={onOpen}
        aria-label={tagline ? `${name}. ${tagline}` : name}
        data-testid={`apps-footer__open-${app.appKey}`}
      >
        {cover ? (
          <Cover src={cover} alt="" loading="lazy" decoding="async" onError={onCoverError} />
        ) : (
          <Glyph aria-hidden="true">
            <Icon size={44} />
          </Glyph>
        )}
        <Name $scrim={Boolean(cover)} aria-hidden="true">
          {name}
        </Name>
      </Face>
      <Reveal>
        <RevealText aria-hidden="true">{tagline || name}</RevealText>
        <RevealRow>
          <OpenPill aria-hidden="true">
            {intl.formatMessage({ id: 'appsFooter.open', defaultMessage: 'Open' })}
          </OpenPill>
          {onAllApps && (
            <PillButton type="button" onClick={onAllApps} data-testid={`apps-footer__all-${app.appKey}`}>
              {intl.formatMessage({ id: 'appsFooter.allApps', defaultMessage: 'All apps' })}
            </PillButton>
          )}
        </RevealRow>
      </Reveal>
    </TileRoot>
  );
};

export default FooterAppTile;
